import { useAuthStore } from '@/stores/auth'
import { getAuthInstance } from '@/firebase/app'
import { onAuthStateChanged } from 'firebase/auth'

// Global auth error handler
let authErrorHandler = null

export function initAuthErrorHandler() {
  if (authErrorHandler) return // Already initialized

  authErrorHandler = {
    showDialog: null, // Will be set by components that can display dialogs
    pendingRedirect: false,
    isRefreshing: false, // Prevent infinite retry loops

    setDialogHandler(showErrorFn) {
      this.showDialog = showErrorFn
    },

    async handleAuthError(error, context = 'API call') {
      console.warn(`Auth error in ${context}:`, error)

      const authStore = useAuthStore()

      // Prevent infinite retry loops
      if (this.isRefreshing) {
        console.warn('Already refreshing token, skipping retry')
        return false
      }

      // Try to refresh token first
      try {
        this.isRefreshing = true
        const user = getAuthInstance().currentUser
        if (user) {
          const freshToken = await user.getIdToken(true) // Force refresh
          // Update the auth store's user with the new token
          if (authStore.user) {
            authStore.user.accessToken = freshToken
          }
          console.log('Token refreshed successfully')

          // If this was during initial login and account is null, retry the login
          if (!authStore.account && user) {
            try {
              const browserLang = navigator.language || navigator.userLanguage
              await authStore.login(user, null, browserLang)
              console.log('Account re-authenticated after token refresh')
            } catch (loginError) {
              console.warn('Failed to re-authenticate after token refresh:', loginError)
              // Don't return true if re-login failed
              this.isRefreshing = false
              return false
            }
          }

          this.isRefreshing = false
          return true // Success, can retry operation
        }
      } catch (refreshError) {
        console.warn('Token refresh failed:', refreshError)
        this.isRefreshing = false
      }

      // Clear auth state
      authStore.logOut()

      // Show error dialog if handler is available
      if (this.showDialog && !this.pendingRedirect) {
        this.pendingRedirect = true

        const locale = (navigator.language || navigator.userLanguage || '').split('-')[0]
        const message =
          locale === 'fr'
            ? 'Votre session a expiré. Vous allez être redirigé vers la page de connexion.'
            : 'Your session has expired. You will be redirected to the login page.'
        const title = locale === 'fr' ? 'Session expirée' : 'Session Expired'

        this.showDialog(message, title)

        // Redirect after showing dialog
        setTimeout(() => {
          if (window.location.pathname !== '/login') {
            const currentPath = window.location.pathname
            window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`
          }
          this.pendingRedirect = false
        }, 3000)
      } else {
        // Fallback: immediate redirect if no dialog handler
        if (window.location.pathname !== '/login') {
          const currentPath = window.location.pathname
          window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`
        }
      }

      return false // Auth failed, don't retry
    }
  }

  // Monitor auth state changes
  onAuthStateChanged(getAuthInstance(), (user) => {
    if (!user) {
      // User logged out or session expired
      console.log('Auth state changed: user logged out')
    } else {
      // User logged in, reset redirect flag
      authErrorHandler.pendingRedirect = false
    }
  })

  return authErrorHandler
}

export function getAuthErrorHandler() {
  if (!authErrorHandler) {
    return initAuthErrorHandler()
  }
  return authErrorHandler
}
